import { useEffect, useState, useCallback } from 'react';

const useNewFilteredRotationLocalStorage = (
  isOrganic,
  stateIsOrganic,
  ownerId,
  ownerLevel,
  cultures,
  from,
) => {
  const filterNameForLocalStorage =
    from === 'groundTypeEffect' ? 'filteredGroundTypeEffects' : 'filteredRotationEffects';

  // Initialisation lazy avec les données du localStorage
  const [activeFilters, setActiveFilters] = useState(() => {
    const storedDataString = localStorage.getItem(filterNameForLocalStorage);
    if (!storedDataString) {
      return { conventional: [], organic: [] };
    }

    const storedData = JSON.parse(storedDataString);
    const userIndex = storedData.maskedRotationsByLevel.findIndex((obj) => {
      switch (ownerLevel) {
        case 'cooperatives':
          return obj?.structure === ownerId;
        case 'sectors':
          return obj?.sector === ownerId;
        case 'exploitations':
          return obj?.exploitation === ownerId;
        default:
          return false;
      }
    });

    if (userIndex !== -1 && storedData.maskedRotationsByLevel[userIndex].activeFilters) {
      return storedData.maskedRotationsByLevel[userIndex].activeFilters;
    }

    return { conventional: [], organic: [] };
  });

  const [selectedFilters, setSelectedFilters] = useState({});

  // Fonction pour trouver l'index de l'utilisateur
  const findUserIndex = useCallback(
    (storedData) => {
      if (!storedData) return -1;

      return storedData.maskedRotationsByLevel.findIndex((obj) => {
        switch (ownerLevel) {
          case 'cooperatives':
            return obj?.structure === ownerId;
          case 'sectors':
            return obj?.sector === ownerId;
          case 'exploitations':
            return obj?.exploitation === ownerId;
          default:
            return false;
        }
      });
    },
    [ownerId, ownerLevel],
  );

  const calculateSelectedFilters = useCallback((filters, culturesList) => {
    const rowFilters = filters.filter(
      (f) => f.targets.includes('rows') && f.property && f.values.length > 0,
    );
    const columnFilters = filters.filter(
      (f) => f.targets.includes('columns') && f.property && f.values.length > 0,
    );

    const rows = {};
    const columns = {};

    culturesList.forEach((culture) => {
      if (rowFilters.length > 0) {
        rows[culture.id] = rowFilters.every((filter) =>
          filter.values.includes(culture[filter.property]),
        );
      } else {
        rows[culture.id] = true;
      }

      if (columnFilters.length > 0) {
        columns[culture.id] = columnFilters.every((filter) =>
          filter.values.includes(culture[filter.property]),
        );
      } else {
        columns[culture.id] = true;
      }
    });

    return { rows, columns };
  }, []);

  // Calculer selectedFilters quand cultures change ou au montage
  useEffect(() => {
    if (!cultures || cultures.length === 0) return;

    const cultureStateOrganic = isOrganic
      ? cultures.filter((c) => c.isOrganic === true)
      : cultures.filter((c) => c.isOrganic === false);

    const currentActiveFilters = activeFilters[stateIsOrganic];

    if (currentActiveFilters && currentActiveFilters.length > 0) {
      setSelectedFilters((prev) => ({
        ...prev,
        [stateIsOrganic]: calculateSelectedFilters(currentActiveFilters, cultureStateOrganic),
      }));
    } else {
      const allVisible = {};
      cultureStateOrganic.forEach((c) => {
        allVisible[c.id] = true;
      });
      setSelectedFilters((prev) => ({
        ...prev,
        [stateIsOrganic]: { rows: allVisible, columns: allVisible },
      }));
    }
  }, [cultures, stateIsOrganic, isOrganic, activeFilters, calculateSelectedFilters]);

  // Sauvegarder dans le localStorage quand activeFilters change
  useEffect(() => {
    const hasActiveFilters = Object.values(activeFilters).some(
      (filters) =>
        filters && filters.length > 0 && filters.some((f) => f.property && f.values.length > 0),
    );

    const storedDataString = localStorage.getItem(filterNameForLocalStorage);
    let storedData = storedDataString ? JSON.parse(storedDataString) : null;
    const userIndex = findUserIndex(storedData);

    if (!hasActiveFilters) {
      if (storedData && userIndex !== -1) {
        if (storedData.maskedRotationsByLevel.length === 1) {
          localStorage.removeItem(filterNameForLocalStorage);
        } else {
          storedData.maskedRotationsByLevel.splice(userIndex, 1);
          localStorage.setItem(filterNameForLocalStorage, JSON.stringify(storedData));
        }
      }
      return;
    }

    const newEntry = {
      ...(ownerLevel === 'cooperatives' && { structure: ownerId }),
      ...(ownerLevel === 'sectors' && { sector: ownerId }),
      ...(ownerLevel === 'exploitations' && { exploitation: ownerId }),
      activeFilters,
    };

    if (storedData) {
      if (userIndex !== -1) {
        storedData.maskedRotationsByLevel[userIndex] = newEntry;
      } else {
        storedData.maskedRotationsByLevel.push(newEntry);
      }
    } else {
      storedData = {
        maskedRotationsByLevel: [newEntry],
      };
    }

    localStorage.setItem(filterNameForLocalStorage, JSON.stringify(storedData));
  }, [activeFilters, ownerId, ownerLevel, filterNameForLocalStorage, findUserIndex]);

  const updateFilters = useCallback(
    (newActiveFilters) => {
      setActiveFilters((prev) => ({
        ...prev,
        [stateIsOrganic]: newActiveFilters,
      }));
    },
    [stateIsOrganic],
  );

  const resetFilters = useCallback(() => {
    setActiveFilters((prev) => ({
      ...prev,
      [stateIsOrganic]: [],
    }));
  }, [stateIsOrganic]);

  return {
    activeFilters: activeFilters[stateIsOrganic] || [],
    selectedFilters,
    updateFilters,
    resetFilters,
  };
};

export default useNewFilteredRotationLocalStorage;
